import {
  SWAP_TYPES,
  TOKENS_MAP,
  TRANSACTION_TYPES,
  CRO,
  WCRO,
} from "../constants"
import { enqueueToast } from "../components/Toastify"
import { useAllContracts, useWethContract } from "./useContract"

import { AppState } from "../state"
import { BigNumber } from "@ethersproject/bignumber"
import { Erc20 } from "../../types/ethers-contracts/Erc20"
import { GasPrices } from "../state/user"
import { MetaSwapDeposit } from "../../types/ethers-contracts/MetaSwapDeposit"
import { SwapFlashLoanNoWithdrawFee } from "../../types/ethers-contracts/SwapFlashLoanNoWithdrawFee"
import { formatDeadlineToNumber } from "../utils"
import { parseUnits } from "@ethersproject/units"
import { subtractSlippage } from "../utils/slippage"
import { updateLastTransactionTimes } from "../state/application"
import { useActiveWeb3React } from "."
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { useCheckAndApproveTokenForTrade } from "src/hooks/useCheckAndApproveTokenForTrade"

type Contracts = {
  swapContract: SwapFlashLoanNoWithdrawFee | MetaSwapDeposit | null
}
type SwapSide = {
  amount: BigNumber
  symbol: string
  poolName: string
  tokenIndex: number
}
type FormState = {
  from: SwapSide
  to: SwapSide & { amountMediumSynth: BigNumber }
  swapType: Exclude<SWAP_TYPES, SWAP_TYPES.INVALID>
}
type ApproveAndSwapStateArgument = FormState & Contracts

export function useApproveAndSwap(): (
  state: ApproveAndSwapStateArgument,
  summary: TransactionSummaryData,
) => Promise<void> {
  const dispatch = useDispatch()
  const tokenContracts = useAllContracts()
  const wcroContract = useWethContract(WCRO)
  const { account, chainId } = useActiveWeb3React()
  const { addTransactions } = useTransactionModal()
  const checkAndApproveTokenForTrade = useCheckAndApproveTokenForTrade()
  const { gasStandard, gasFast, gasInstant } = useSelector(
    (state: AppState) => state.application,
  )
  const {
    slippageCustom,
    slippageSelected,
    gasPriceSelected,
    gasCustom,
    transactionDeadlineCustom,
    transactionDeadlineSelected,
    infiniteApproval,
  } = useSelector((state: AppState) => state.user)
  return async function approveAndSwap(
    state: ApproveAndSwapStateArgument,
    summary: TransactionSummaryData,
  ): Promise<void> {
    try {
      if (!account) throw new Error("Wallet must be connected")
      if (!state.swapContract) throw new Error("Swap contract is not loaded")
      if (chainId === undefined) throw new Error("Unknown chain")
      // For each token being deposited, check the allowance and approve it if necessary
      const fromSymbol =
        state.from.symbol === CRO.symbol ? WCRO.symbol : state.from.symbol
      const tokenContract = tokenContracts?.[fromSymbol] as Erc20
      let gasPrice
      if (gasPriceSelected === GasPrices.Custom) {
        gasPrice = gasCustom?.valueSafe
      } else if (gasPriceSelected === GasPrices.Fast) {
        gasPrice = gasFast
      } else if (gasPriceSelected === GasPrices.Instant) {
        gasPrice = gasInstant
      } else {
        gasPrice = gasStandard
      }
      gasPrice = parseUnits(gasPrice ? String(gasPrice) : "45", 9)
      if (
        tokenContract == null ||
        (state.swapType === SWAP_TYPES.WRAPPED && wcroContract == null)
      )
        return
      const addressToApprove = state.swapContract?.address ?? ""
      const token = TOKENS_MAP[fromSymbol]
      await checkAndApproveTokenForTrade(
        tokenContract,
        addressToApprove,
        account,
        state.from.amount,
        infiniteApproval,
        gasPrice,
        {
          onTransactionError: () => {
            throw new Error("Approval failed")
          },
        },
        token,
      )
      if (
        state.swapType === SWAP_TYPES.DIRECT ||
        state.swapType === SWAP_TYPES.WRAPPED
      ) {
        const deadline = formatDeadlineToNumber(
          transactionDeadlineSelected,
          transactionDeadlineCustom,
        )
        const args = [
          state.from.tokenIndex,
          state.to.tokenIndex,
          state.from.amount,
          subtractSlippage(state.to.amount, slippageSelected, slippageCustom),
          Math.round(new Date().getTime() / 1000 + 60 * deadline),
        ] as const
        console.debug("swap - direct", args)
        const swapTxt = async () => {
          return await (
            state.swapContract as NonNullable<
              typeof state.swapContract // we already check for nonnull above
            >
          ).swap(...args)
        }
        const transactions = [swapTxt]
        if (state.swapType === SWAP_TYPES.WRAPPED) {
          if (state.from.symbol === CRO.symbol) {
            const wrapTxt = async () => {
              return await (
                wcroContract as NonNullable<
                  typeof wcroContract // we already check for nonnull above
                >
              ).deposit({
                value: state.from.amount.toHexString(),
              })
            }
            transactions.unshift(wrapTxt)
          } else if (state.to.symbol === CRO.symbol) {
            const unwrapTxt = async () => {
              return await (
                wcroContract as NonNullable<
                  typeof wcroContract // we already check for nonnull above
                >
              ).withdraw(state.to.amount.toHexString())
            }
            transactions.push(unwrapTxt)
          }
        }
        await addTransactions(transactions, summary)
      } else {
        throw new Error("Invalid Swap Type, or contract not loaded")
      }

      dispatch(
        updateLastTransactionTimes({
          [TRANSACTION_TYPES.SWAP]: Date.now(),
        }),
      )
      return Promise.resolve()
    } catch (e) {
      console.error(e)
      enqueueToast(
        "error",
        e instanceof Error
          ? e.message
          : "Swap failed, your tokens have been reverted",
        "Failed",
      )
    }
  }
}
